import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { Unavailable } from "../pages/Unavailable.tsx";
import { DEPLOYMENT_FACTS_KEY, DeploymentFactsContext } from "./context.ts";
import { readDeploymentFacts } from "./facts.ts";

/**
 * Reads `/gate/deployment` before anything that depends on it renders (#158,
 * #161). While the answer is outstanding this renders nothing: a page shown
 * with a guessed chain or RP ID is worse than a blank moment, because an RP
 * ID is permanent for every credential registered under it (ADR-0010
 * decision 2).
 */
export function RequireDeploymentFacts({ children }: { children: ReactNode }) {
  const facts = useQuery({
    queryKey: DEPLOYMENT_FACTS_KEY,
    queryFn: () => readDeploymentFacts(fetch),
    // Read once per page load and never refetched: the facts change with a
    // deploy, and a deploy ships a new index.html anyway.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (facts.isError) {
    return <Unavailable onRetry={() => void facts.refetch()} />;
  }
  if (!facts.isSuccess) return null;

  return (
    <DeploymentFactsContext value={facts.data}>
      {children}
    </DeploymentFactsContext>
  );
}
